// PSI Flow Types - End-to-end procurement and sales flow tracking

import type { PurchaseOrder } from './purchaseorder';
import type { SalesQuotation, SalesOrder } from './salesorder';
import type { InboundShipment } from './inbound';
import type { Invoice } from './invoice';
import type { Return } from './returns';

// Flow Type
export type FlowType = 'procurement' | 'sales';

// Flow Stage
export type FlowStage =
  | 'purchase_order'
  | 'inbound'
  | 'inventory'
  | 'invoice'
  | 'quotation'
  | 'sales_order'
  | 'delivery'
  | 'return';

// Flow Status (overall flow status)
export type FlowStatus = 'in_progress' | 'completed' | 'overdue' | 'cancelled';

// PSI Flow Summary (for list view)
export interface PSIFlowSummary {
  flow_id: string;
  flow_type: FlowType;
  counterparty: string;          // vendor or customer name
  amount: number;
  currency: string;
  current_stage: FlowStage;
  status: FlowStatus;
  is_overdue: boolean;
  created_at: string;
  doc_number: string;            // primary document number (PO or SO number)
}

// PSI Flow Detail (full flow with linked documents)
export interface PSIFlowDetail {
  flow_id: string;
  flow_type: FlowType;
  purchase_order?: PurchaseOrder;
  inbounds?: InboundShipment[];
  sales_quotation?: SalesQuotation;
  sales_order?: SalesOrder;
  invoices?: Invoice[];
  returns?: Return[];
}

// Flow Filter for list queries
export interface FlowFilter {
  flow_type?: FlowType;
  status?: FlowStatus;
  counterparty?: string;
  current_stage?: FlowStage;
  is_overdue?: boolean;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Flow List Response
export interface FlowListResponse {
  flows: PSIFlowSummary[];
  total: number;
  limit: number;
  offset: number;
}

// Flow Statistics Response
export interface FlowStatsResponse {
  total_flows: number;
  in_progress: number;
  completed: number;
  overdue: number;
  procurement_flows: number;
  sales_flows: number;
}

// Flow status colors
export const FLOW_STATUS_COLORS: Record<FlowStatus, string> = {
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

// Flow stage labels (en and zh)
export const FLOW_STAGE_LABELS: Record<FlowStage, { en: string; zh: string }> = {
  purchase_order: { en: 'Purchase Order', zh: '採購單' },
  inbound: { en: 'Inbound', zh: '進貨' },
  inventory: { en: 'Inventory', zh: '庫存' },
  invoice: { en: 'Invoice', zh: '發票' },
  quotation: { en: 'Quotation', zh: '報價' },
  sales_order: { en: 'Sales Order', zh: '銷售單' },
  delivery: { en: 'Delivery', zh: '出貨' },
  return: { en: 'Return', zh: '退貨' },
};

// Flow type labels
export const FLOW_TYPE_LABELS: Record<FlowType, { en: string; zh: string }> = {
  procurement: { en: 'Procurement', zh: '採購流程' },
  sales: { en: 'Sales', zh: '銷售流程' },
};

// Flow status labels
export const FLOW_STATUS_LABELS: Record<FlowStatus, { en: string; zh: string }> = {
  in_progress: { en: 'In Progress', zh: '進行中' },
  completed: { en: 'Completed', zh: '已完成' },
  overdue: { en: 'Overdue', zh: '逾期' },
  cancelled: { en: 'Cancelled', zh: '已取消' },
};
